import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  GitCompare, Trophy, Target, Users, 
  TrendingUp, TrendingDown, Minus, Loader2,
  ArrowRight, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comparison Bar Component
const ComparisonBar = ({ label, value1, value2, name1, name2, higherIsBetter = true }) => {
  const max = Math.max(value1, value2, 1);
  const pct1 = (value1 / max) * 100;
  const pct2 = (value2 / max) * 100;
  
  const winner1 = higherIsBetter ? value1 > value2 : value1 < value2;
  const winner2 = higherIsBetter ? value2 > value1 : value2 < value1;
  const tie = value1 === value2;

  return (
    <div className="py-4 border-b last:border-b-0">
      <div className="text-sm font-medium text-center mb-3">{label}</div>
      <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
        {/* Team 1 */}
        <div className="text-right">
          <div className="flex items-center justify-end gap-2 mb-1">
            <span className={`text-2xl font-bold ${winner1 && !tie ? 'text-green-600' : ''}`}>
              {value1}
            </span>
            {winner1 && !tie && <Trophy className="w-4 h-4 text-yellow-500" />}
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${winner1 && !tie ? 'bg-green-500' : 'bg-primary'}`}
              style={{ width: `${pct1}%`, marginLeft: 'auto' }}
            />
          </div>
        </div>
        
        {/* VS */}
        <div className="text-muted-foreground text-sm font-medium">vs</div>
        
        {/* Team 2 */}
        <div className="text-left">
          <div className="flex items-center gap-2 mb-1">
            {winner2 && !tie && <Trophy className="w-4 h-4 text-yellow-500" />}
            <span className={`text-2xl font-bold ${winner2 && !tie ? 'text-green-600' : ''}`}>
              {value2}
            </span>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${winner2 && !tie ? 'bg-green-500' : 'bg-accent'}`}
              style={{ width: `${pct2}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Team Stats Summary Card
const TeamSummaryCard = ({ team, isWinner }) => {
  if (!team) return null;
  
  const winRate = team.wins + team.losses > 0 
    ? ((team.wins / (team.wins + team.losses)) * 100).toFixed(1)
    : 0;
  
  return (
    <Card className={`${isWinner ? 'border-2 border-green-500' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {team.logo_url && (
              <img src={team.logo_url} alt={team.name} className="w-12 h-12 rounded-lg object-cover" />
            )}
            <div>
              <CardTitle className="text-lg">{team.name}</CardTitle>
              <CardDescription>{team.age_category} • {team.gender}</CardDescription>
            </div>
          </div>
          {isWinner && (
            <Badge className="bg-green-500">
              <Trophy className="w-3 h-3 mr-1" />
              Better Overall
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-3xl font-bold">{team.wins}-{team.losses}</p>
            <p className="text-sm text-muted-foreground">Record</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{winRate}%</p>
            <p className="text-sm text-muted-foreground">Win Rate</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Team Comparison Page
export const TeamComparison = () => {
  const [teams, setTeams] = useState([]);
  const [team1Id, setTeam1Id] = useState('');
  const [team2Id, setTeam2Id] = useState('');
  const [team1Data, setTeam1Data] = useState(null);
  const [team2Data, setTeam2Data] = useState(null);
  const [team1Players, setTeam1Players] = useState([]);
  const [team2Players, setTeam2Players] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`);
      setTeams(res.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamData = async (teamId, setData, setPlayers) => {
    try {
      const [teamRes, playersRes] = await Promise.all([
        axios.get(`${API}/teams/${teamId}`),
        axios.get(`${API}/teams/${teamId}/players`)
      ]);
      setData(teamRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      console.error('Error fetching team data:', error);
    }
  };

  const handleCompare = async () => {
    if (!team1Id || !team2Id) {
      toast.error('Please select two teams to compare');
      return;
    }
    if (team1Id === team2Id) {
      toast.error('Please select different teams');
      return;
    }
    
    setComparing(true);
    await Promise.all([
      fetchTeamData(team1Id, setTeam1Data, setTeam1Players),
      fetchTeamData(team2Id, setTeam2Data, setTeam2Players)
    ]);
    setComparing(false);
  };

  // Calculate aggregate player stats
  const getPlayerStatsAggregate = (players) => {
    return players.reduce((acc, player) => {
      acc.touchdowns += (player.stats?.touchdowns || 0);
      acc.interceptions += (player.stats?.interceptions || 0);
      acc.passingYards += (player.stats?.passing_yards || 0);
      acc.rushingYards += (player.stats?.rushing_yards || 0);
      acc.receivingYards += (player.stats?.receiving_yards || 0);
      return acc;
    }, { touchdowns: 0, interceptions: 0, passingYards: 0, rushingYards: 0, receivingYards: 0 });
  };

  // Determine overall winner
  const getOverallWinner = () => {
    if (!team1Data || !team2Data) return null;
    
    let team1Score = 0;
    let team2Score = 0;
    
    // Compare wins
    if (team1Data.wins > team2Data.wins) team1Score++;
    else if (team2Data.wins > team1Data.wins) team2Score++;
    
    // Compare points scored
    if ((team1Data.points_scored || 0) > (team2Data.points_scored || 0)) team1Score++;
    else if ((team2Data.points_scored || 0) > (team1Data.points_scored || 0)) team2Score++;
    
    // Compare point differential
    const diff1 = (team1Data.points_scored || 0) - (team1Data.points_allowed || 0);
    const diff2 = (team2Data.points_scored || 0) - (team2Data.points_allowed || 0);
    if (diff1 > diff2) team1Score++;
    else if (diff2 > diff1) team2Score++;
    
    if (team1Score > team2Score) return 'team1';
    if (team2Score > team1Score) return 'team2';
    return null;
  };

  const stats1 = getPlayerStatsAggregate(team1Players);
  const stats2 = getPlayerStatsAggregate(team2Players);
  const winner = getOverallWinner();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <GitCompare className="h-8 w-8" />
          Team Comparison
        </h1>
        <p className="text-muted-foreground mt-2">
          Compare two teams side-by-side to see how they stack up
        </p>
      </div>

      {/* Team Selection */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-4 items-end">
            <div>
              <label className="text-sm font-medium mb-2 block">Team 1</label>
              <Select value={team1Id} onValueChange={setTeam1Id}>
                <SelectTrigger data-testid="team1-select">
                  <SelectValue placeholder="Select first team..." />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id} disabled={team.id === team2Id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-center">
              <Button 
                onClick={handleCompare} 
                disabled={!team1Id || !team2Id || comparing}
                className="gap-2"
                data-testid="compare-btn"
              >
                {comparing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GitCompare className="h-4 w-4" />
                )}
                Compare
              </Button>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Team 2</label>
              <Select value={team2Id} onValueChange={setTeam2Id}>
                <SelectTrigger data-testid="team2-select">
                  <SelectValue placeholder="Select second team..." />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id} disabled={team.id === team1Id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {team1Data && team2Data && (
        <div className="space-y-6">
          {/* Team Summary Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            <TeamSummaryCard team={team1Data} isWinner={winner === 'team1'} />
            <TeamSummaryCard team={team2Data} isWinner={winner === 'team2'} />
          </div>

          {/* Detailed Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Head-to-Head Comparison
              </CardTitle>
              <CardDescription>
                {team1Data.name} vs {team2Data.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ComparisonBar 
                label="Wins"
                value1={team1Data.wins || 0}
                value2={team2Data.wins || 0}
                name1={team1Data.name}
                name2={team2Data.name}
              />
              <ComparisonBar 
                label="Losses"
                value1={team1Data.losses || 0}
                value2={team2Data.losses || 0}
                name1={team1Data.name}
                name2={team2Data.name}
                higherIsBetter={false}
              />
              <ComparisonBar 
                label="Points Scored"
                value1={team1Data.points_scored || 0}
                value2={team2Data.points_scored || 0}
                name1={team1Data.name}
                name2={team2Data.name}
              />
              <ComparisonBar 
                label="Points Allowed"
                value1={team1Data.points_allowed || 0}
                value2={team2Data.points_allowed || 0}
                name1={team1Data.name}
                name2={team2Data.name}
                higherIsBetter={false}
              />
              <ComparisonBar 
                label="Point Differential"
                value1={(team1Data.points_scored || 0) - (team1Data.points_allowed || 0)}
                value2={(team2Data.points_scored || 0) - (team2Data.points_allowed || 0)}
                name1={team1Data.name}
                name2={team2Data.name}
              />
            </CardContent>
          </Card>

          {/* Player Stats Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Player Statistics
              </CardTitle>
              <CardDescription>
                Aggregate player stats for each team
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ComparisonBar 
                label="Total Touchdowns"
                value1={stats1.touchdowns}
                value2={stats2.touchdowns}
                name1={team1Data.name}
                name2={team2Data.name}
              />
              <ComparisonBar 
                label="Interceptions (Caught)"
                value1={stats1.interceptions}
                value2={stats2.interceptions}
                name1={team1Data.name}
                name2={team2Data.name}
              />
              <ComparisonBar 
                label="Passing Yards"
                value1={stats1.passingYards}
                value2={stats2.passingYards}
                name1={team1Data.name}
                name2={team2Data.name}
              />
              <ComparisonBar 
                label="Rushing Yards"
                value1={stats1.rushingYards}
                value2={stats2.rushingYards}
                name1={team1Data.name}
                name2={team2Data.name}
              />
              <ComparisonBar 
                label="Receiving Yards"
                value1={stats1.receivingYards}
                value2={stats2.receivingYards}
                name1={team1Data.name}
                name2={team2Data.name}
              />
            </CardContent>
          </Card>

          {/* Roster Size Comparison */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{team1Data.name} Roster</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{team1Players.length} players</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {['QB', 'WR', 'RB', 'TE', 'DB', 'LB', 'DL'].map(pos => {
                    const count = team1Players.filter(p => p.position === pos).length;
                    if (count === 0) return null;
                    return (
                      <Badge key={pos} variant="secondary">{pos}: {count}</Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{team2Data.name} Roster</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{team2Players.length} players</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {['QB', 'WR', 'RB', 'TE', 'DB', 'LB', 'DL'].map(pos => {
                    const count = team2Players.filter(p => p.position === pos).length;
                    if (count === 0) return null;
                    return (
                      <Badge key={pos} variant="secondary">{pos}: {count}</Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!team1Data && !team2Data && (
        <Card>
          <CardContent className="py-16 text-center">
            <GitCompare className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Select Teams to Compare</h3>
            <p className="text-muted-foreground">
              Choose two teams above and click Compare to see detailed statistics
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
